import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import {
  Building2,
  Car,
  FileText,
  ShoppingCart,
  TrendingUp,
  Users as UsersIcon,
} from 'lucide-react'
import {
  hendelsessenterAPI,
  userAPI,
  HendelsessenterResponse,
} from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { formatLeasingselskap } from '../utils/formatLeasingselskap'
import { localizeApiDetail } from '../i18n/apiError'

type TypeFilter = 'alle' | 'eie' | 'leasing' | 'solgt' | 'kjopt'

const PAGE_SIZE = 100

/** Stored instead of a translated fallback, so the message is translated at
 *  render and follows a language switch. */
const LOAD_FAILED = 'loadFailed'

interface WatchedOrg {
  org_number: string
  org_name: string | null
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultFromDate(): string {
  const now = new Date()
  return `${now.getFullYear()}-01-01`
}

function defaultToDate(): string {
  return toLocalDateStr(new Date())
}

function formatDateNo(iso: string): string {
  // Backend sends "YYYY-MM-DD" — render as DD.MM.YYYY for grid display.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso
}

function formatDateTimeNo(iso: string): string {
  // Backend sends "YYYY-MM-DDTHH:MM:SS" — render as DD.MM.YYYY HH:MM.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso)
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : iso
}

export default function Hendelsessenter() {
  const { t } = useTranslation('hendelser')
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse optional deep-link params (?org=...&dag=YYYY-MM-DD&type=eie|leasing)
  const urlOrg = searchParams.get('org') ?? ''
  const urlDag = searchParams.get('dag') ?? ''
  const urlRawType = searchParams.get('type')
  const urlType: TypeFilter =
    urlRawType === 'eie' ||
    urlRawType === 'leasing' ||
    urlRawType === 'solgt' ||
    urlRawType === 'kjopt'
      ? urlRawType
      : 'alle'

  // When a dag is supplied, narrow the from/to range to that single day so the
  // user lands directly on the event. Otherwise use the standard defaults.
  const urlFrom = urlDag || defaultFromDate()
  const urlTo = urlDag || defaultToDate()

  // Form (pending) state for filter inputs
  const [fromInput, setFromInput] = useState<string>(urlFrom)
  const [toInput, setToInput] = useState<string>(urlTo)
  const [orgInput, setOrgInput] = useState<string>(urlOrg)
  const [typeInput, setTypeInput] = useState<TypeFilter>(urlType)

  // Applied state (used in actual query)
  const [appliedFrom, setAppliedFrom] = useState<string>(urlFrom)
  const [appliedTo, setAppliedTo] = useState<string>(urlTo)
  const [appliedOrg, setAppliedOrg] = useState<string>(urlOrg)
  const [appliedType, setAppliedType] = useState<TypeFilter>(urlType)
  const [page, setPage] = useState<number>(1)

  // Focused event (when deep-linked): used to surface a banner with context
  const focusedDag = urlDag || null
  const focusedOrg = urlOrg || null
  const focusedType: TypeFilter = urlType

  // Re-sync filter state when the URL changes (e.g. user clicks a new bell
  // item while already on this page). Without this, navigation within the
  // SPA to a new ?org&dag&type would be silently ignored because the state
  // was only seeded once at mount.
  useEffect(() => {
    setFromInput(urlFrom)
    setToInput(urlTo)
    setOrgInput(urlOrg)
    setTypeInput(urlType)
    setAppliedFrom(urlFrom)
    setAppliedTo(urlTo)
    setAppliedOrg(urlOrg)
    setAppliedType(urlType)
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrg, urlDag, urlRawType])

  const [watchedOrgs, setWatchedOrgs] = useState<WatchedOrg[]>([])
  const [data, setData] = useState<HendelsessenterResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Load watched orgs once for the org dropdown
  useEffect(() => {
    let alive = true
    userAPI
      .getMineOrganisasjoner()
      .then((res) => {
        if (!alive) return
        const items = (res.data?.items ?? []) as WatchedOrg[]
        setWatchedOrgs(items)
      })
      .catch(() => {
        /* non-fatal — the filter dropdown just stays empty */
      })
    return () => {
      alive = false
    }
  }, [])

  // Fetch hendelsessenter when applied filters change
  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    const params: {
      from: string
      to: string
      org?: string
      type?: 'eie' | 'leasing' | 'solgt' | 'kjopt'
      page: number
      page_size: number
    } = {
      from: appliedFrom,
      to: appliedTo,
      page,
      page_size: PAGE_SIZE,
    }
    if (appliedOrg) params.org = appliedOrg
    if (appliedType !== 'alle') params.type = appliedType
    hendelsessenterAPI
      .getHendelsessenter(params)
      .then((res) => {
        if (!alive) return
        setData(res.data)
        setLoading(false)
      })
      .catch((err) => {
        if (!alive) return
        setError(
          localizeApiDetail(err?.response?.data?.detail) ||
            err?.message ||
            LOAD_FAILED
        )
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [appliedFrom, appliedTo, appliedOrg, appliedType, page])

  // Keep URL query in sync with applied org
  useEffect(() => {
    const current = searchParams.get('org') ?? ''
    if (current !== appliedOrg) {
      const next = new URLSearchParams(searchParams)
      if (appliedOrg) next.set('org', appliedOrg)
      else next.delete('org')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedOrg])

  const applyFilters = () => {
    setAppliedFrom(fromInput)
    setAppliedTo(toInput)
    setAppliedOrg(orgInput)
    setAppliedType(typeInput)
    setPage(1)
  }

  const selectType = (type: TypeFilter) => {
    setTypeInput(type)
    setAppliedType(type)
    setPage(1)
  }

  const selectOrgRow = (orgNr: string) => {
    setOrgInput(orgNr)
    setAppliedOrg(orgNr)
    setPage(1)
  }

  const clearOrg = () => {
    setOrgInput('')
    setAppliedOrg('')
    setPage(1)
  }

  const sortedRows = useMemo(() => {
    if (!data) return []
    return [...data.rows].sort((a, b) => (a.registrert < b.registrert ? 1 : -1))
  }, [data])

  const hasNoData = data && data.summary.total_cars === 0
  const pagination = data?.pagination
  const totalPages = pagination?.total_pages ?? 0
  const total = pagination?.total ?? 0
  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const pageEnd = Math.min(page * PAGE_SIZE, total)

  return (
    <div data-testid="hendelsessenter-page" className="p-6 space-y-4">
      {/* Page header */}
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('title')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('subtitle')}
        </p>
      </div>

      {/* Deep-link focus banner */}
      {focusedDag && focusedOrg && (
        <div
          data-testid="focused-event-banner"
          className="card p-4 border-l-4 border-primary-500 bg-primary-50 dark:bg-primary-900/20"
        >
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <Trans
              t={t}
              i18nKey={focusedType !== 'alle' ? 'bannerWithType' : 'banner'}
              values={{
                org: focusedOrg,
                name: (() => {
                  const name = watchedOrgs.find((o) => o.org_number === focusedOrg)?.org_name
                  return name ? ` (${formatLeasingselskap(name)})` : ''
                })(),
                dag: focusedDag,
                type:
                  focusedType === 'eie'
                    ? t('types.eie')
                    : focusedType === 'leasing'
                    ? t('types.leasing')
                    : focusedType === 'solgt'
                    ? t('types.solgtBruktbil')
                    : t('types.kjoptBruktbil'),
              }}
              components={{
                org: <span className="font-mono font-semibold" />,
                dag: <span className="font-semibold" />,
                type: <span className="font-semibold" />,
              }}
            />
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('filter.fromDate')}
            </label>
            <input
              data-testid="filter-from"
              type="date"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('filter.toDate')}
            </label>
            <input
              data-testid="filter-to"
              type="date"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('filter.organisation')}
            </label>
            <select
              data-testid="filter-org"
              value={orgInput}
              onChange={(e) => {
                setOrgInput(e.target.value)
                setAppliedOrg(e.target.value)
                setPage(1)
              }}
              className="input-field"
            >
              <option value="">{t('filter.all')}</option>
              {watchedOrgs.map((o) => (
                <option key={o.org_number} value={o.org_number}>
                  {o.org_name ? `${formatLeasingselskap(o.org_name)} (${o.org_number})` : o.org_number}
                </option>
              ))}
            </select>
          </div>

          <div data-testid="filter-type" className="flex gap-2 flex-wrap">
            {(['alle', 'eie', 'leasing', 'solgt', 'kjopt'] as TypeFilter[]).map((type) => (
              <button
                key={type}
                data-testid={`filter-type-${type}`}
                onClick={() => selectType(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  typeInput === type
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {t(`types.${type}`)}
              </button>
            ))}
          </div>

          <button
            data-testid="filter-apply"
            onClick={applyFilters}
            className="btn-primary"
          >
            {t('filter.apply')}
          </button>

          {appliedOrg && (
            <button
              onClick={clearOrg}
              className="text-sm text-primary-700 dark:text-primary-300 hover:underline"
            >
              {t('filter.clearOrg')}
            </button>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error === LOAD_FAILED ? t('loadFailed') : error} />}

      {loading && !data ? (
        <LoadingSpinner />
      ) : data ? (
        <>
          {/* Summary cards */}
          <div
            data-testid="summary-cards"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4"
          >
            <div className="card p-4 flex items-center gap-3">
              <Car className="w-8 h-8 text-primary-600" />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('summary.totalCars')}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {data.summary.total_cars.toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary-600" />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('summary.totalOrgs')}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {data.summary.total_orgs.toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <UsersIcon className="w-8 h-8 text-primary-600" />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('types.eie')}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {data.summary.eie_count.toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary-600" />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('types.leasing')}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {data.summary.leasing_count.toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-rose-600 dark:text-rose-400" />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('types.solgtBruktbil')}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {(data.summary.solgt_count ?? 0).toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <ShoppingCart className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('types.kjoptBruktbil')}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {(data.summary.kjopt_count ?? 0).toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
          </div>

          {hasNoData ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('noHits')}
            </div>
          ) : (
            <>
              {/* Per-org grid */}
              <div className="card overflow-x-auto" data-testid="per-org-grid">
                <div className="p-6 pb-2">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {t('perOrg.title')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('perOrg.hint')}
                  </p>
                </div>
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-left">{t('detail.orgnr')}</th>
                      <th className="text-left">{t('perOrg.navn')}</th>
                      <th className="text-right">{t('perOrg.totalt')}</th>
                      <th className="text-right">{t('types.eie')}</th>
                      <th className="text-right">{t('types.leasing')}</th>
                      <th className="text-right">{t('types.solgt')}</th>
                      <th className="text-right">{t('types.kjopt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_org.map((row) => {
                      const isActive = appliedOrg === row.org_nr
                      return (
                        <tr
                          key={row.org_nr}
                          onClick={() => selectOrgRow(row.org_nr)}
                          className={`cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            isActive
                              ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-300 dark:ring-primary-700'
                              : ''
                          }`}
                        >
                          <td className="font-mono">{row.org_nr}</td>
                          <td>{row.org_navn ?? '—'}</td>
                          <td className="text-right font-semibold">
                            {row.total.toLocaleString('nb-NO')}
                          </td>
                          <td className="text-right">
                            {row.eie.toLocaleString('nb-NO')}
                          </td>
                          <td className="text-right">
                            {row.leasing.toLocaleString('nb-NO')}
                          </td>
                          <td className="text-right">
                            {(row.solgt ?? 0).toLocaleString('nb-NO')}
                          </td>
                          <td className="text-right">
                            {(row.kjopt ?? 0).toLocaleString('nb-NO')}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Detail grid */}
              <div className="card overflow-x-auto" data-testid="detail-grid">
                <div className="p-6 pb-2">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {t('detail.title')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('detail.showing', {
                      from: pageStart.toLocaleString('nb-NO'),
                      to: pageEnd.toLocaleString('nb-NO'),
                      total: total.toLocaleString('nb-NO'),
                    })}
                  </p>
                </div>
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-left whitespace-nowrap" style={{ minWidth: '110px' }}>{t('detail.dato')}</th>
                      <th className="text-left whitespace-nowrap">{t('detail.kilde')}</th>
                      <th className="text-left whitespace-nowrap">{t('detail.hendelsetidspunkt')}</th>
                      <th className="text-left whitespace-nowrap">{t('detail.orgnr')}</th>
                      <th className="text-left">{t('detail.orgNavn')}</th>
                      <th className="text-left whitespace-nowrap" style={{ minWidth: '140px' }}>{t('detail.type')}</th>
                      <th className="text-left">{t('detail.merke')}</th>
                      <th className="text-left">{t('detail.modell')}</th>
                      <th className="text-left whitespace-nowrap">{t('detail.postnummer')}</th>
                      <th className="text-left">{t('detail.poststed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, idx) => {
                      const typeLabel =
                        row.type === 'eie'
                          ? t('types.eie')
                          : row.type === 'leasing'
                          ? t('types.leasing')
                          : row.type === 'solgt'
                          ? t('types.solgtBruktbil')
                          : t('types.kjoptBruktbil')
                      const typeClass =
                        row.type === 'eie'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                          : row.type === 'leasing'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                          : row.type === 'solgt'
                          ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      const sourceLabel =
                        row.source === 'bruktbil' ? t('source.bruktbil') : t('source.nyreg')
                      const sourceClass =
                        row.source === 'bruktbil'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      const ht = row.hendelse_tidspunkt
                        ? formatDateTimeNo(row.hendelse_tidspunkt)
                        : '—'
                      return (
                        <tr
                          key={`${row.org_nr}-${row.registrert}-${idx}`}
                          data-testid="detail-row"
                        >
                          <td className="whitespace-nowrap">
                            {formatDateNo(row.registrert)}
                          </td>
                          <td>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${sourceClass}`}
                            >
                              {sourceLabel}
                            </span>
                          </td>
                          <td className="text-gray-600 dark:text-gray-400">
                            {ht}
                          </td>
                          <td className="font-mono">{row.org_nr}</td>
                          <td>{row.org_navn ?? '—'}</td>
                          <td>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap inline-block ${typeClass}`}
                            >
                              {typeLabel}
                            </span>
                          </td>
                          <td>{row.merke}</td>
                          <td>{row.modell}</td>
                          <td className="font-mono">{row.postnummer}</td>
                          <td>{row.poststed}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                <div
                  data-testid="detail-pagination"
                  className="flex items-center justify-between gap-3 p-4 border-t border-gray-200 dark:border-gray-700"
                >
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t('pagination.pageOf', {
                      page,
                      pages: Math.max(1, totalPages),
                      total: total.toLocaleString('nb-NO'),
                    })}
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid="page-prev"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-3 py-1 rounded-md text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('pagination.prev')}
                    </button>
                    <button
                      data-testid="page-next"
                      onClick={() =>
                        setPage((p) => Math.min(Math.max(1, totalPages), p + 1))
                      }
                      disabled={page >= totalPages}
                      className="px-3 py-1 rounded-md text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('pagination.next')}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  )
}
